import { formatImportDate, importDateInput } from "./importDates";
import { Fragment, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import ReviewTemplateSelect from "../../components/Common/ReviewTemplateSelect";
import {
  AppOffcanvas,
  Button,
  Input,
} from "../../components/Common/AppOverlay";
import "./ImportedEngagementCorrectionDrawer.scss";

const normaliseField = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const normalizedClientValue = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const firstValue = (row, names, fallback = "") => {
  const entry = Object.entries(row || {}).find(([key]) =>
    names.some((name) => normaliseField(key) === normaliseField(name))
  );
  return entry?.[1] ?? fallback;
};

const toDateText = formatImportDate;
const inputDateValue = importDateInput;

const isRiskField = (key) => key.startsWith("risk");

const riskValueForDisplay = (value) => {
  if (value === "—" || value === "" || value === null || value === undefined)
    return "—";
  if (/^(y|yes|true|1)$/i.test(String(value))) return "Yes";
  if (/^(n|no|false|0)$/i.test(String(value))) return "No";
  return String(value);
};

const fieldIssueName = (field) => {
  const key = normaliseField(field);
  if (isRiskField(key)) return field;
  if (/client(name|code|firm)/.test(key)) return "Client / Firm";
  if (/reviewtemplate|template/.test(key)) return "Review Template";
  if (/engagementname/.test(key)) return "Engagement Name";
  if (/engagementtype|worktype/.test(key)) return "Work Type";
  if (/industry/.test(key)) return "Industry";
  return field;
};

const groupFor = (field) => {
  const key = normaliseField(field);
  if (isRiskField(key)) return "Risk Assessment";
  if (/client(name|code|firm)/.test(key)) return "Client / Firm";
  if (/reviewtemplate|template/.test(key)) return "Review Setup";
  if (/partner|manager|reviewer|assignedto|team/.test(key))
    return "Team & Roles";
  if (/date|period|financialyearend|status|external|source/.test(key)) return "Dates & Metadata";
  return "Engagement";
};

const editableGroups = ["Engagement", "Client / Firm"];

const groupIcons = {
  Engagement: "bx-briefcase",
  "Client / Firm": "bx-buildings",
};

function StatusBadge({ value }) {
  const tone = /not supplied|read only/i.test(value)
    ? "ignored"
    : /error|required/i.test(value)
    ? "error"
    : /changed/i.test(value)
    ? "changed"
    : /warning|needs/i.test(value)
    ? "warning"
    : "success";
  return (
    <span className={`engagement-import__badge is-${tone}`}>
      {tone === "success" && <i className="bx bx-check" />}
      {tone === "changed" && <i className="bx bx-edit-alt" />}
      {tone === "warning" && <i className="bx bx-error" />}
      {tone === "error" && <i className="bx bx-error-circle" />}
      {value}
    </span>
  );
}

export default function ImportedEngagementCorrectionDrawer({
  clients,
  industryTypes,
  mappings,
  row,
  reviewTemplates,
  users,
  workTypes,
  onApply,
  onClose,
  onOpenNewClientDrawer,
  validateRow,
}) {
  const [clientAction, setClientAction] = useState(null);
  const [draft, setDraft] = useState(null);
  const [rawDataOpen, setRawDataOpen] = useState(false);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);

  useEffect(() => {
    setClientAction(null);
    setRawDataOpen(false);
    setShowIssuesOnly(false);
    setDraft(row ? { ...row } : null);
  }, [row?.id]);

  const reviewedRow = useMemo(
    () => (draft && validateRow ? validateRow(draft) : draft),
    [draft, validateRow]
  );

  if (!row || !reviewedRow) return null;

  // Show all validation results, including fields outside the editable groups.
  const relevantValidationIssues = reviewedRow.validationIssues || [];
  const relevantWarningIssues = reviewedRow.warningIssues || [];
  const issueByField = new Map(
    relevantValidationIssues.map((issue) => [issue.field, issue])
  );
  const activeMappings = (mappings || []).filter(
    (mapping) => !mapping.ignore && mapping.destination
  );
  const reviewMappings = activeMappings.filter((mapping) =>
    editableGroups.includes(groupFor(mapping.destination))
  );
  const selectedClient = clients.find(
    (client) => String(client.id) === String(reviewedRow.clientId)
  );
  const nameMatch = clients.find(
    (client) =>
      normalizedClientValue(client.name) === normalizedClientValue(row.client)
  );
  const codeMatch = clients.find(
    (client) =>
      normalizedClientValue(client.code) ===
      normalizedClientValue(row.clientCode)
  );
  const showClientResolution = Boolean(
    issueByField.has("Client / Firm") || row.clientMatchIssue || clientAction
  );

  const fieldConfigFor = (field) => {
    const key = normaliseField(field);
    if (isRiskField(key)) return { property: key, type: "risk" };
    if (/engagementcode/.test(key))
      return { property: "engagementCode", type: "text" };
    if (/engagementname/.test(key)) return { property: "name", type: "text" };
    if (key === "clienttype") return { property: "clientType", type: "text" };
    if (/engagementtype|worktype/.test(key))
      return { property: "workType", type: "select", options: workTypes };
    if (/industry/.test(key))
      return { property: "industry", type: "select", options: industryTypes };
    if (/description/.test(key))
      return { property: "description", type: "textarea" };
    if (/periodenddate|duedate/.test(key))
      return { property: "dueDate", type: "date" };
    if (/leadreviewer/.test(key))
      return { property: "leadReviewer", type: "user" };
    if (/manager/.test(key)) return { property: "manager", type: "user" };
    if (/partner/.test(key)) return { property: "partner", type: "user" };
    if (/assignedto/.test(key)) return { property: "assignedTo", type: "user" };
    if (/externalengagementid/.test(key))
      return { property: "externalEngagementId", type: "text" };
    if (/status/.test(key)) return { property: "isActive", type: "status" };
    if (/reviewtemplate|template/.test(key))
      return { property: "assignedTemplate", type: "template" };
    return { property: key, type: "text" };
  };

  const importedValueFor = (mapping) => {
    if (mapping.virtual) return reviewedRow.suggestedTemplate || "—";
    const value = firstValue(reviewedRow.source, [
      mapping.source,
      mapping.destination,
    ]);
    if (/date/i.test(mapping.destination || "")) return toDateText(value);
    return value === "" || value === null || value === undefined
      ? "—"
      : String(value);
  };

  const resolvedValueFor = (field, importedValue) => {
    const key = normaliseField(field);
    if (isRiskField(key))
      return riskValueForDisplay(reviewedRow[key] ?? importedValue);
    if (/engagementcode/.test(key)) return reviewedRow.engagementCode;
    if (/engagementname/.test(key)) return reviewedRow.name;
    if (key === "clienttype") return reviewedRow.clientType ?? importedValue;
    if (/engagementtype|worktype/.test(key)) return reviewedRow.workType;
    if (/industry/.test(key)) return reviewedRow.industry;
    if (/description/.test(key)) return reviewedRow.description;
    if (/clientname/.test(key))
      return selectedClient?.name || reviewedRow.client;
    if (/clientcode/.test(key))
      return selectedClient?.code || reviewedRow.clientCode;
    if (/reviewtemplate|template/.test(key))
      return reviewedRow.assignedTemplate;
    if (/periodenddate|duedate/.test(key))
      return toDateText(reviewedRow.dueDate);
    if (/leadreviewer/.test(key)) return reviewedRow.leadReviewer;
    if (/manager/.test(key)) return reviewedRow.manager;
    if (/partner/.test(key)) return reviewedRow.partner;
    if (/assignedto/.test(key)) return reviewedRow.assignedTo;
    if (/externalengagementid/.test(key))
      return reviewedRow.externalEngagementId;
    if (/status/.test(key)) return reviewedRow.isActive ? "Active" : "Inactive";
    return importedValue;
  };

  const isChangedField = (field) => {
    const key = normaliseField(field);
    if (/client(name|code|firm)/.test(key))
      return Boolean(
        reviewedRow.overrides?.clientId ||
          reviewedRow.overrides?.client ||
          reviewedRow.overrides?.clientCode ||
          (reviewedRow.clientId &&
            String(reviewedRow.clientId) !== String(row.clientId || ""))
      );
    if (/reviewtemplate|template/.test(key))
      return Boolean(
        reviewedRow.overrides?.assignedTemplate ||
          reviewedRow.assignedTemplate !== row.assignedTemplate
      );
    return Boolean(reviewedRow.overrides?.[fieldConfigFor(field).property]);
  };

  const rawValueFor = (mapping) => {
    if (mapping.virtual) return "";
    return firstValue(reviewedRow.source, [
      mapping.destination,
      mapping.source,
    ]);
  };

  const updateMappedField = (mapping, value) => {
    const config = fieldConfigFor(mapping.destination);
    const configuredValue =
      config.type === "status" ? value === "Active" : value;
    setDraft((current) => {
      const overrides = { ...(current.overrides || {}) };
      const originalValue =
        overrides[config.property]?.originalValue ??
        row.overrides?.[config.property]?.originalValue ??
        row[config.property] ??
        rawValueFor(mapping);
      if (String(configuredValue ?? "") === String(originalValue ?? ""))
        delete overrides[config.property];
      else
        overrides[config.property] = {
          changedAt: new Date().toISOString(),
          configuredValue,
          originalValue,
        };
      return { ...current, [config.property]: configuredValue, overrides };
    });
  };

  const undoMappedField = (mapping) => {
    const config = fieldConfigFor(mapping.destination);
    setDraft((current) => {
      const overrides = { ...(current.overrides || {}) };
      const originalValue =
        overrides[config.property]?.originalValue ??
        row.overrides?.[config.property]?.originalValue ??
        row[config.property] ??
        rawValueFor(mapping);
      delete overrides[config.property];
      return { ...current, [config.property]: originalValue, overrides };
    });
  };

  const chooseClient = (client, resolution) => {
    if (!client) return;
    setClientAction("selected");
    setDraft((current) => {
      const nextValues = {
        clientId: client.id,
        client: client.name,
        clientCode: client.code || current.clientCode,
      };
      const overrides = { ...(current.overrides || {}) };
      Object.entries(nextValues).forEach(([property, value]) => {
        const originalValue =
          overrides[property]?.originalValue ??
          row.overrides?.[property]?.originalValue ??
          row[property];
        if (String(value ?? "") === String(originalValue ?? ""))
          delete overrides[property];
        else
          overrides[property] = {
            changedAt: new Date().toISOString(),
            configuredValue: value,
            originalValue,
          };
      });
      return {
        ...current,
        ...nextValues,
        createClient: false,
        clientResolution: resolution,
        clientMatchIssue: "",
        overrides,
      };
    });
  };

  const undoClientResolution = () => {
    setClientAction(null);
    setDraft((current) => {
      const overrides = { ...(current.overrides || {}) };
      const restored = {};
      ["clientId", "client", "clientCode"].forEach((property) => {
        restored[property] =
          overrides[property]?.originalValue ??
          row.overrides?.[property]?.originalValue ??
          row[property];
        delete overrides[property];
      });
      return {
        ...current,
        ...restored,
        clientMatchIssue: row.clientMatchIssue,
        clientResolution: row.clientResolution,
        overrides,
      };
    });
  };

  const userLabel = (user) =>
    user.fullName ||
    user.name ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    user.email ||
    "Unnamed user";

  const optionsFor = (items) =>
    (items || []).map((item) => ({
      value: String(item.id ?? item.value ?? item.name),
      label: item.name || item.label || String(item.value || ""),
    }));

  const renderEditor = (mapping, resolvedValue, issue) => {
    const config = fieldConfigFor(mapping.destination);
    const selectClass = `engagement-import__drawer-select correction-wizard__select${
      issue ? " correction-wizard__select--error" : ""
    }`;
    if (config.type === "template") {
      return (
        <div>
          <ReviewTemplateSelect
            aria-label={`Review template for ${row.name}`}
            className={selectClass}
            classNamePrefix="drawer-select"
            isClearable
            templates={reviewTemplates}
            value={
              reviewTemplates.find(
                (template) => template.name === reviewedRow.assignedTemplate
              )?.id
            }
            onChange={(template) =>
              updateMappedField(mapping, template?.name || "")
            }
            placeholder="Optional - search review templates"
          />
          <small>
            You can assign a review template later in Edit Engagement.
          </small>
        </div>
      );
    }
    if (config.type === "risk") {
      const options = [
        { value: "Y", label: "Yes" },
        { value: "N", label: "No" },
      ];
      return (
        <Select
          aria-label={`Change ${mapping.destination}`}
          className={selectClass}
          classNamePrefix="drawer-select"
          isClearable
          onChange={(option) => updateMappedField(mapping, option?.value || "")}
          options={options}
          placeholder="Select Yes or No..."
          value={
            options.find((option) => option.label === resolvedValue) || null
          }
        />
      );
    }
    if (config.type === "user") {
      const options = users.map((user) => ({
        value: String(user.id),
        label: userLabel(user),
      }));
      return (
        <Select
          aria-label={`Change ${mapping.destination}`}
          className={selectClass}
          classNamePrefix="drawer-select"
          isClearable
          isSearchable
          noOptionsMessage={() => "No active users found"}
          onChange={(option) => updateMappedField(mapping, option?.label || "")}
          options={options}
          placeholder={`Select ${mapping.destination.toLowerCase()}...`}
          value={
            options.find((option) => option.label === resolvedValue) ||
            (resolvedValue && resolvedValue !== "—"
              ? { value: resolvedValue, label: resolvedValue }
              : null)
          }
        />
      );
    }
    if (config.type === "select") {
      const options = optionsFor(config.options);
      return (
        <Select
          aria-label={`Change ${mapping.destination}`}
          className={selectClass}
          classNamePrefix="drawer-select"
          isClearable
          isSearchable
          onChange={(option) => updateMappedField(mapping, option?.label || "")}
          options={options}
          placeholder={`Select ${mapping.destination.toLowerCase()}...`}
          value={
            options.find((option) => option.label === resolvedValue) ||
            (resolvedValue && resolvedValue !== "—"
              ? { value: resolvedValue, label: resolvedValue }
              : null)
          }
        />
      );
    }
    if (config.type === "date")
      return (
        <Input
          aria-label={`Change ${mapping.destination}`}
          className={`correction-wizard__input${issue ? " is-invalid" : ""}`}
          onChange={(event) => updateMappedField(mapping, event.target.value)}
          type="date"
          value={inputDateValue(reviewedRow[config.property])}
        />
      );
    if (config.type === "status")
      return (
        <Input
          aria-label={`Change ${mapping.destination}`}
          className={`correction-wizard__input${issue ? " is-invalid" : ""}`}
          onChange={(event) => updateMappedField(mapping, event.target.value)}
          type="select"
          value={reviewedRow.isActive ? "Active" : "Inactive"}
        >
          <option>Active</option>
          <option>Inactive</option>
        </Input>
      );
    if (config.type === "textarea")
      return (
        <Input
          aria-label={`Change ${mapping.destination}`}
          className={`correction-wizard__input correction-wizard__textarea${
            issue ? " is-invalid" : ""
          }`}
          onChange={(event) => updateMappedField(mapping, event.target.value)}
          rows="3"
          type="textarea"
          value={reviewedRow[config.property] ?? resolvedValue ?? ""}
        />
      );
    return (
      <Input
        aria-label={`Change ${mapping.destination}`}
        className={`correction-wizard__input${issue ? " is-invalid" : ""}`}
        onChange={(event) => updateMappedField(mapping, event.target.value)}
        type="text"
        value={reviewedRow[config.property] ?? resolvedValue ?? ""}
      />
    );
  };

  const changedMappings = reviewMappings.filter((mapping) =>
    isChangedField(mapping.destination)
  );
  const changedCount = changedMappings.length;
  const changedIssueCount = new Set(
    changedMappings
      .map((mapping) => fieldIssueName(mapping.destination))
      .filter((field) => issueByField.has(field))
  ).size;
  const canApplyChanges = changedIssueCount === 0;
  const issueCount = relevantValidationIssues.length;
  const warningCount = relevantWarningIssues.length;
  const matchedCount = reviewMappings.filter((mapping) => {
    const issue = issueByField.get(fieldIssueName(mapping.destination));
    return (
      !issue &&
      !isChangedField(mapping.destination) &&
      importedValueFor(mapping) !== "—"
    );
  }).length;
  const displayedGroups = editableGroups
    .map((name) => ({
      name,
      rows: reviewMappings.filter(
        (mapping) =>
          groupFor(mapping.destination) === name &&
          (!showIssuesOnly ||
            issueByField.has(fieldIssueName(mapping.destination)))
      ),
    }))
    .filter((group) => group.rows.length);

  return (
    <AppOffcanvas
      backdrop
      backdropClassName="engagement-import__drawer-backdrop"
      className="engagement-import__drawer correction-wizard"
      direction="end"
      isOpen={Boolean(row)}
      toggle={onClose}
    >
      <header className="correction-wizard__header">
        <div>
          <h2>Edit Imported Engagement</h2>
          <b>{row.name}</b>
          <small>{row.source?.__sourceLocation || `Excel row ${row.sourceRow}`}</small>
        </div>
        <button
          aria-label="Close correction wizard"
          onClick={onClose}
          type="button"
        >
          <i className="bx bx-x" />
        </button>
      </header>

      <div className="correction-wizard__workspace correction-wizard__workspace--single-page">
        <div className="correction-wizard__main">
          <div className="engagement-import__drawer-body correction-wizard__body">
            <div className="correction-wizard__summary-row">
              <div className="engagement-import__mapping-summary">
                <span className="is-error">
                  <i className="bx bx-error-circle" /> <b>{issueCount}</b>{" "}
                  issues
                </span>
                <span className="is-success">
                  <i className="bx bx-check-circle" /> <b>{matchedCount}</b>{" "}
                  matched
                </span>
                <span className="is-changed">
                  <i className="bx bx-edit-alt" /> <b>{changedCount}</b> changed
                </span>
                <span className="is-warning">
                  <i className="bx bx-error" /> <b>{warningCount}</b> require
                  review
                </span>
              </div>
              <label className="correction-wizard__issues-only">
                <input
                  checked={showIssuesOnly}
                  onChange={(event) => setShowIssuesOnly(event.target.checked)}
                  type="checkbox"
                />
                Show only fields needing attention
              </label>
            </div>

            {(issueCount > 0 || warningCount > 0) && (
              <section
                className={`engagement-import__mapping-attention correction-wizard__attention${
                  issueCount ? "" : " correction-wizard__attention--warning"
                }`}
              >
                <i className="bx bx-error-circle" />
                <div>
                  <h3>
                    {issueCount
                      ? `${issueCount} fields need attention`
                      : "Review recommended"}
                  </h3>
                  <p>
                    {issueCount
                      ? "Resolve the highlighted fields before creating this engagement."
                      : "Review these warnings before continuing."}
                  </p>
                  <ul>
                    {[
                      ...relevantValidationIssues,
                      ...relevantWarningIssues,
                    ].map((issue) => (
                      <li
                        className={
                          relevantValidationIssues.includes(issue)
                            ? "correction-wizard__issue--error"
                            : "correction-wizard__issue--warning"
                        }
                        key={`${issue.field}-${issue.message}`}
                      >
                        <span>
                          <b>{issue.field}</b> — {issue.message}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            <div className="correction-wizard__step-heading">
              <div>
                <h3>Imported engagement details</h3>
              </div>
              <p>
                Update PIERS values while keeping the original Excel values
                visible for comparison.
              </p>
            </div>

            {displayedGroups.length ? (
              <div className="correction-wizard__groups">
                {displayedGroups.map((group) => (
                  <section
                    className="correction-wizard__group"
                    key={group.name}
                  >
                    <header>
                      <i className={`bx ${groupIcons[group.name]}`} />
                      <h3>{group.name}</h3>
                    </header>
                    <div className="correction-wizard__grid">
                      {group.rows.map((mapping) => {
                        const importedValue = importedValueFor(mapping);
                        const issue = issueByField.get(
                          fieldIssueName(mapping.destination)
                        );
                        const changed = isChangedField(mapping.destination);
                        const key = normaliseField(mapping.destination);
                        const isClientField = /client(name|code|firm)/.test(
                          key
                        );
                        const resolvedValue = resolvedValueFor(
                          mapping.destination,
                          importedValue
                        );
                        return (
                          <div
                            className={`correction-wizard__field${
                              issue ? " has-issue" : ""
                            }${changed ? " is-changed" : ""}`}
                            key={`${mapping.source}-${mapping.destination}`}
                          >
                            <div className="correction-wizard__field-heading">
                              <label>
                                {mapping.destination}
                                {mapping.required && <em>*</em>}
                              </label>
                              <StatusBadge
                                value={
                                  issue
                                    ? "Required"
                                    : changed
                                    ? "Changed"
                                    : isClientField && reviewedRow.createClient
                                    ? "Valid"
                                    : importedValue === "—"
                                    ? "Not supplied"
                                    : "Matched"
                                }
                              />
                            </div>
                            {isClientField ? (
                              <div
                                className={`correction-wizard__readonly${
                                  issue ? " is-invalid" : ""
                                }`}
                              >
                                {resolvedValue || "—"}
                              </div>
                            ) : (
                              renderEditor(mapping, resolvedValue, issue)
                            )}
                            <div className="correction-wizard__field-meta">
                              <small>
                                Imported value: <b>{importedValue}</b>
                              </small>
                              {changed && (
                                <button
                                  onClick={() =>
                                    isClientField
                                      ? undoClientResolution()
                                      : undoMappedField(mapping)
                                  }
                                  type="button"
                                >
                                  <i className="bx bx-undo" /> Undo
                                </button>
                              )}
                            </div>
                            {issue && (
                              <p className="correction-wizard__field-error">
                                <i className="bx bx-error-circle" />{" "}
                                {issue.message}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {group.name === "Client / Firm" && showClientResolution && (
                      <div className="engagement-import__client-resolution correction-wizard__client-resolution">
                        <header>
                          <i className="bx bx-git-branch" />
                          <div>
                            <b>Resolve client match</b>
                            <small>
                              Imported name: “{row.client || "—"}” · Code: “
                              {row.clientCode || "—"}”
                            </small>
                          </div>
                        </header>
                        <div>
                          {nameMatch && (
                            <label
                              className={
                                String(reviewedRow.clientId) ===
                                String(nameMatch.id)
                                  ? "is-selected"
                                  : ""
                              }
                            >
                              <input
                                checked={
                                  String(reviewedRow.clientId) ===
                                  String(nameMatch.id)
                                }
                                name={`client-resolution-${row.id}`}
                                onChange={() =>
                                  chooseClient(
                                    nameMatch,
                                    "Existing client selected by imported name"
                                  )
                                }
                                type="radio"
                              />
                              <span>
                                <b>Use client matched by name</b>
                                <strong>{nameMatch.name}</strong>
                                <small>Code: {nameMatch.code || "—"}</small>
                              </span>
                            </label>
                          )}
                          {codeMatch && (
                            <label
                              className={
                                String(reviewedRow.clientId) ===
                                String(codeMatch.id)
                                  ? "is-selected"
                                  : ""
                              }
                            >
                              <input
                                checked={
                                  String(reviewedRow.clientId) ===
                                  String(codeMatch.id)
                                }
                                name={`client-resolution-${row.id}`}
                                onChange={() =>
                                  chooseClient(
                                    codeMatch,
                                    "Existing client selected by imported code"
                                  )
                                }
                                type="radio"
                              />
                              <span>
                                <b>Use client matched by code</b>
                                <strong>{codeMatch.name}</strong>
                                <small>Code: {codeMatch.code || "—"}</small>
                              </span>
                            </label>
                          )}
                          <label
                            className={
                              clientAction === "search" ? "is-selected" : ""
                            }
                          >
                            <input
                              checked={clientAction === "search"}
                              name={`client-resolution-${row.id}`}
                              onChange={() => setClientAction("search")}
                              type="radio"
                            />
                            <span>
                              <b>Search for another client</b>
                              <small>Select any active PIERS client</small>
                            </span>
                          </label>
                          <button
                            type="button"
                            onClick={() =>
                              onOpenNewClientDrawer((client) =>
                                chooseClient(client, "New client created")
                              )
                            }
                          >
                            <i className="bx bx-plus-circle" />
                            <span>
                              <b>Create an engagement client record</b>
                              <small>
                                Use the imported values as a starting point
                              </small>
                            </span>
                          </button>
                        </div>
                        {clientAction === "search" && (
                          <Select
                            aria-label={`Existing client for ${row.name}`}
                            className="engagement-import__drawer-select engagement-import__client-search"
                            classNamePrefix="drawer-select"
                            isSearchable
                            noOptionsMessage={() => "No clients found"}
                            onChange={(option) =>
                              chooseClient(
                                option?.client,
                                "Existing client selected"
                              )
                            }
                            options={clients.map((client) => ({
                              value: String(client.id),
                              label: `${client.name}${
                                client.code ? ` (${client.code})` : ""
                              }`,
                              client,
                            }))}
                            placeholder="Search clients..."
                            value={
                              selectedClient
                                ? {
                                    value: String(selectedClient.id),
                                    label: `${selectedClient.name}${
                                      selectedClient.code
                                        ? ` (${selectedClient.code})`
                                        : ""
                                    }`,
                                    client: selectedClient,
                                  }
                                : null
                            }
                          />
                        )}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            ) : (
              <div className="correction-wizard__empty">
                <i className="bx bx-check-circle" />
                <b>No imported engagement fields need attention.</b>
                <span>
                  Turn off the issue filter to review all imported values.
                </span>
              </div>
            )}

            {rawDataOpen && (
              <section className="engagement-import__raw-data correction-wizard__raw-data">
                <h3>Original Excel data</h3>
                <dl>
                  {Object.entries(reviewedRow.source || {})
                    .filter(([field]) => !field.startsWith("__source"))
                    .map(([field, value]) => (
                      <Fragment key={field}>
                        <dt>{field}</dt>
                        <dd>
                          {value === null || value === "" ? "—" : String(value)}
                        </dd>
                      </Fragment>
                    ))}
                </dl>
              </section>
            )}
          </div>
        </div>
      </div>
      <footer className="correction-wizard__footer">
        <button
          className="engagement-import__raw-toggle"
          onClick={() => setRawDataOpen((current) => !current)}
          type="button"
        >
          <i className="bx bx-file" />{" "}
          {rawDataOpen ? "Hide Original Data" : "View Original Data"}
        </button>
        <span className={issueCount ? "has-errors" : "is-ready"}>
          <i
            className={`bx ${
              issueCount ? "bx-error-circle" : "bx-check-circle"
            }`}
          />
          <b>
            {issueCount
              ? `${issueCount} blocking issue${
                  issueCount === 1 ? "" : "s"
                } remain`
              : "All required fields resolved"}
          </b>
          <small>
            {changedIssueCount
              ? "Correct the invalid changed fields before saving."
              : issueCount
              ? "Valid corrections can be saved while other issues remain. For fields not editable here, correct the original file or return to Upload to review date order, then revalidate."
              : "This record is ready to create."}
          </small>
        </span>
        <div>
          <Button outline onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={!canApplyChanges}
            onClick={() => onApply(row.id, reviewedRow, changedCount)}
          >
            {changedCount ? "Save Corrections & Revalidate" : "Revalidate"}
          </Button>
        </div>
      </footer>
    </AppOffcanvas>
  );
}
